import React, { forwardRef, useEffect, useRef, useState, useMemo } from 'react';
import { Typography, Box } from '@material-ui/core';
import cx from 'classnames';

import { CardWrapper } from '../UI/CardWrapper';
import { ShareButton } from '../UI/ShareButton';
import { getSocialShareImage, translate } from '../../utils/helpers';
import CloseIcon from '../../assets/icons/CloseIcon';
import { colorsPalette } from '../../theme';
import LinkedInIcon from '../../assets/icons/LinkedInIcon';
import FacebookIcon from '../../assets/icons/FacebookIcon';
import TwitterIcon from '../../assets/icons/TwitterIcon';
import MicrosoftTeamsIcon from '../../assets/icons/MicrosoftTeamsIcon';
import useStyles from './styles';
import { TwitterShareButton, FacebookShareButton, LinkedinShareButton } from 'react-share';
import { IKImage } from '../IKImage';

interface ShareModalProps {
  handleClose: () => void;
  resultHash?: string | null;
  pathwayName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
  slug?: string | null;
  tenantName?: string | null;
}

const ShareModal = forwardRef<HTMLDivElement, ShareModalProps>(
  ({ handleClose, resultHash, pathwayName, cpdCredits, category, tenantName }, ref) => {
    const classes = useStyles();

    const teamsRef = useRef<HTMLDivElement | null>(null);
    const [teamsLink, setTeamsLink] = useState('');
    const url = window?.location?.origin;
    const shareLink = `${url}/share/${resultHash}`;

    const shareText =
      pathwayName && cpdCredits
        ? translate('page.test.shareText', { pathwayName, cpdCredits })
        : '';
    const shareTwitterText =
      pathwayName && category
        ? translate('page.test.shareTwitterText', { pathwayName, category })
        : '';
    const shareFacebookText =
      pathwayName && category
        ? translate('page.test.shareFacebookText', { pathwayName, category })
        : '';

    const socialShareImgUrl = useMemo(() => getSocialShareImage(resultHash, tenantName), [
      resultHash,
      tenantName,
    ]);

    const handleOpenTeamsShare = () => {
      window.open(teamsLink, 'Share to Microsoft Teams', 'width=700,height=600');
    };

    useEffect(() => {
      //used to call microsoft library for share link generation
      //@ts-ignore
      window?.shareToMicrosoftTeams?.renderButtons && window.shareToMicrosoftTeams.renderButtons();
    }, []);

    useEffect(() => {
      const child = teamsRef?.current?.children?.item(0);
      //@ts-ignore
      if (child?.href) {
        //@ts-ignore
        setTeamsLink(child?.href);
      }
    }, []);

    return (
      <CardWrapper ref={ref} wrapperClass={classes.wrapper}>
        <Box className={classes.headerWrapper}>
          <Typography variant="h2" className={classes.title}>
            {translate('page.test.shareYourSuccess')}
          </Typography>
          <CloseIcon
            color={colorsPalette.gray}
            className={classes.closeIcon}
            onClick={handleClose}
          />
        </Box>
        <Box className={classes.content}>
          <div className={classes.certificateImgWrapper}>
            <div className={classes.relativeWrapper}>
              <div className={classes.certificateImgWrapperWithBg}>
                <IKImage
                  className={classes.certificateImg}
                  path={socialShareImgUrl}
                  alt={translate('page.test.share')}
                  srcset={[{ width: 750 }]}
                  sizes="100vw"
                />
              </div>
            </div>
          </div>
          <Box className={classes.buttonsWrapper}>
            <LinkedinShareButton
              className={classes.shareButtonWrapper}
              title={shareText}
              url={shareLink}
              resetButtonStyle={true}
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <LinkedInIcon color={colorsPalette.white} />
                {translate('page.test.linkedIn')}
              </ShareButton>
            </LinkedinShareButton>
            <ShareButton wrapperClass={classes.shareButton} onClick={handleOpenTeamsShare}>
              <MicrosoftTeamsIcon color={colorsPalette.white} />
              {translate('page.test.microsoftTeams')}
            </ShareButton>
            <FacebookShareButton quote={shareFacebookText} url={shareLink} resetButtonStyle={true}>
              <ShareButton wrapperClass={classes.shareButton}>
                <FacebookIcon color={colorsPalette.white} />
                {translate('page.test.facebook')}
              </ShareButton>
            </FacebookShareButton>
            <TwitterShareButton
              className={classes.shareButtonWrapper}
              title={shareTwitterText}
              url={shareLink}
              resetButtonStyle={true}
              hashtags={['FinanceUnlocked', 'finance', 'education', 'learning']}
            >
              <ShareButton wrapperClass={classes.shareButton}>
                <TwitterIcon color={colorsPalette.white} />
                {translate('page.test.twitter')}
              </ShareButton>
            </TwitterShareButton>
            <div
              className={cx('teams-share-button', classes.teamsShareOriginal)}
              data-href={shareLink}
              data-msg-text={shareText}
              ref={teamsRef}
            />
          </Box>
        </Box>
      </CardWrapper>
    );
  },
);

export default ShareModal;
